/**
 * The four window KPIs of the sync-run page.
 *
 * PRESENTATION ONLY. Every figure arrives finished: the counts, the success rate
 * and the average duration are SQL aggregates over the whole window (proved in
 * `backend/tests/test_bexio_sync_run_read_postgres.py`), and the last successful
 * sync comes from `/status`. This component reads none of them itself and derives
 * none of them from the table rows — a reduce over one page of 25 would answer a
 * different question under the same label.
 *
 * TWO SOURCES, TWO FAILURE STATES. The first card is the status read; the other
 * three are the summary read. They are passed in as separate loading/error pairs
 * because they fail separately upstream, and collapsing them here would blank a
 * card that has perfectly good data.
 */
import React from "react";
import { AlertTriangle, CheckCircle2, Clock, Timer } from "lucide-react";

import { formatDateTime } from "../../../utils/datetime";
import { relativeFromNow } from "../../../components/admin/bexio/bexioFormat";
import BexioKpiFilterCard from "../../../components/admin/bexio/workspace/BexioKpiFilterCard";
import {
  fmtCount,
  formatDurationSeconds,
  formatSuccessRate,
} from "./bexioSyncRunsView";

export default function BexioSyncRunsKpiRow({
  statusLoading,
  statusError,
  lastSync,
  summaryLoading,
  summaryError,
  summary,
  rangeLabel,
}) {
  const successRateText = formatSuccessRate(summary?.success_rate);

  return (
    <section
      aria-label="Kennzahlen"
      className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
    >
      {/* KPI 1 — from `/status`, NOT from the first table row. That endpoint
          restricts the answer to DATA_SYNC_SCOPES and to finished successes,
          so a newer `currency_repair` run cannot present itself as the moment
          data last arrived. */}
      <BexioKpiFilterCard
        icon={Clock}
        tone="primary"
        label="Letzter erfolgreicher Sync"
        value={
          statusLoading
            ? "…"
            : statusError
            ? "—"
            : lastSync?.finished_at
            ? formatDateTime(lastSync.finished_at)
            : "Noch kein erfolgreicher Daten-Sync"
        }
        hint={
          statusError
            ? "Status konnte nicht geladen werden"
            : lastSync?.finished_at
            ? relativeFromNow(lastSync.finished_at) || undefined
            : undefined
        }
      />

      <BexioKpiFilterCard
        icon={CheckCircle2}
        tone="good"
        label="Erfolgreiche Läufe"
        value={
          summaryLoading ? "…" : summaryError ? "—" : fmtCount(summary?.success_count)
        }
        /* The rate is already a percentage from SQL. When nothing in the window
           has finished it is null, and the honest line is that there is nothing
           to rate — not "0 % erfolgreich", which claims every run failed. */
        hint={
          summaryError
            ? "Kennzahlen nicht verfügbar"
            : successRateText
            ? `${successRateText} erfolgreich · ${rangeLabel}`
            : `Keine abgeschlossenen Läufe · ${rangeLabel}`
        }
      />

      <BexioKpiFilterCard
        icon={AlertTriangle}
        tone={summary?.error_count ? "bad" : "neutral"}
        label="Fehlerhafte Läufe"
        value={
          summaryLoading ? "…" : summaryError ? "—" : fmtCount(summary?.error_count)
        }
        /* `partial` is counted separately rather than added in: a partial run
           did land data, and folding it into an unlabelled error figure would
           overstate the failures. Silent when there are none. */
        hint={
          summaryError
            ? "Kennzahlen nicht verfügbar"
            : summary?.partial_count
            ? `${summary.partial_count} teilweise · ${rangeLabel}`
            : rangeLabel
        }
      />

      <BexioKpiFilterCard
        icon={Timer}
        tone="neutral"
        label="Ø Dauer"
        value={
          summaryLoading
            ? "…"
            : summaryError
            ? "—"
            : summary?.average_duration_seconds == null
            ? "Keine abgeschlossenen Läufe"
            : formatDurationSeconds(summary.average_duration_seconds)
        }
        hint={summaryError ? "Kennzahlen nicht verfügbar" : rangeLabel}
      />
    </section>
  );
}
